import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../api';
import { money, useLedger, useMyTenants } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';
import type { Member, TenantRole } from '../types';

/** Настройки тенанта: параметры выставления, участники и состояние книг. */
export function SettingsPage() {
  const { tenantId = '' } = useParams();
  const queryClient = useQueryClient();
  const me = useMyTenants();
  const ledger = useLedger(tenantId);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState({ userId: '', role: 'ADMIN' as TenantRole });
  const [settings, setSettings] = useState<{
    invoiceDueDays?: number;
    billingDay?: number;
    invoiceNumberPrefix?: string;
    receiptAgentSign?: string;
  }>({});

  const current = me.data?.find((t) => t.tenant.id === tenantId);
  const tenant = current?.tenant;
  const isOwner = current?.role === 'OWNER' || current?.platformAdmin;

  const members = useQuery({
    queryKey: ['members', tenantId],
    queryFn: () => api.get<Member[]>(`/tenants/${tenantId}/members`),
  });

  const save = useMutation({
    mutationFn: () => api.patch(`/tenants/${tenantId}`, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-tenants'] });
      setSettings({});
    },
    onError: (err) => setError((err as ApiError).message),
  });

  const addMember = useMutation({
    mutationFn: () => api.post(`/tenants/${tenantId}/members`, invite),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members', tenantId] });
      setInviting(false);
      setInvite({ userId: '', role: 'ADMIN' });
    },
    onError: (err) => setError((err as ApiError).message),
  });

  const removeMember = useMutation({
    mutationFn: (memberId: string) => api.del(`/tenants/${tenantId}/members/${memberId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members', tenantId] }),
    onError: (err) => setError((err as ApiError).message),
  });

  if (!tenant) return <div className="state">Загрузка…</div>;

  return (
    <>
      <div className="page-head">
        <h1>Настройки</h1>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="col">
        <div className="card">
          <div className="card-head">
            <h2>Тенант</h2>
          </div>
          <div className="grid-3">
            <div>
              <div className="lbl">Название</div>
              <div className="val">{tenant.name}</div>
            </div>
            <div>
              <div className="lbl">Код</div>
              <div className="val">{tenant.code}</div>
            </div>
            <div>
              <div className="lbl">Ваша роль</div>
              <div className="val">
                {ROLE_LABELS[current?.role ?? ''] ?? (current?.platformAdmin ? 'платформа' : '—')}
              </div>
            </div>
          </div>
          <div className="form-hint">
            Часовой пояс: {tenant.timezone} · валюта: {tenant.currency}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Выставление счетов</h2>
          </div>
          <div className="row wrap">
            <div className="form-group">
              <label className="form-label" htmlFor="set-day">
                День выставления
              </label>
              <input
                id="set-day"
                className="input"
                type="number"
                min={1}
                max={28}
                defaultValue={tenant.billingDay}
                disabled={!isOwner}
                onChange={(e) => setSettings({ ...settings, billingDay: Number(e.target.value) })}
              />
              <div className="form-hint">Счета за прошлый период выставляются в этот день.</div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="set-due">
                Срок оплаты, дней
              </label>
              <input
                id="set-due"
                className="input"
                type="number"
                min={0}
                defaultValue={tenant.invoiceDueDays}
                disabled={!isOwner}
                onChange={(e) => setSettings({ ...settings, invoiceDueDays: Number(e.target.value) })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="set-prefix">
                Префикс номеров
              </label>
              <input
                id="set-prefix"
                className="input"
                defaultValue={tenant.invoiceNumberPrefix}
                disabled={!isOwner}
                onChange={(e) => setSettings({ ...settings, invoiceNumberPrefix: e.target.value })}
              />
            </div>
          </div>
          {isOwner && (
            <button
              className="btn btn-primary btn-sm"
              disabled={Object.keys(settings).length === 0 || save.isPending}
              onClick={() => { setError(null); save.mutate(); }}
            >
              Сохранить
            </button>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Чеки разовых продаж</h2>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="set-agent">
              Признак агента
            </label>
            <select
              id="set-agent"
              className="input"
              defaultValue={tenant.receiptAgentSign ?? 'NONE'}
              disabled={!isOwner}
              onChange={(e) => setSettings({ ...settings, receiptAgentSign: e.target.value })}
            >
              <option value="NONE">Нет — продажа от своего имени</option>
              <option value="AGENT">Агент</option>
              <option value="COMMISSION_AGENT">Комиссионер</option>
              <option value="ATTORNEY">Поверенный</option>
              <option value="PAYING_AGENT">Платёжный агент</option>
              <option value="PAYING_SUBAGENT">Платёжный субагент</option>
              <option value="BANK_PAYING_AGENT">Банковский платёжный агент</option>
              <option value="BANK_PAYING_SUBAGENT">Банковский платёжный субагент</option>
            </select>
            <div className="form-hint">
              Когда признак задан, площадка принимает деньги за исполнителя: в чеке печатаются
              признак агента и данные поставщика, поэтому к продаже обязательны ИНН и имя
              исполнителя. Уже пробитые чеки остаются как были — заказ хранит свой снимок.
            </div>
          </div>
          {isOwner && (
            <button
              className="btn btn-primary btn-sm"
              disabled={settings.receiptAgentSign === undefined || save.isPending}
              onClick={() => { setError(null); save.mutate(); }}
            >
              Сохранить
            </button>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Участники</h2>
            {isOwner && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setError(null); setInviting(true); }}
              >
                <Icon name="plus" />
                Добавить
              </button>
            )}
          </div>
          {members.isLoading ? (
            <div className="state">Загрузка…</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Роль</th>
                  {isOwner && <th />}
                </tr>
              </thead>
              <tbody>
                {(members.data ?? []).map((m) => (
                  <tr key={m.id}>
                    <td className="muted small">
                      <code>{m.userId}</code>
                    </td>
                    <td>
                      <span className="badge badge-gray">{ROLE_LABELS[m.role] ?? m.role}</span>
                    </td>
                    {isOwner && (
                      <td className="num">
                        <button
                          className="btn btn-secondary btn-xs"
                          onClick={() => removeMember.mutate(m.id)}
                        >
                          Убрать
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="form-hint">
            Владелец правит настройки и состав, администратор ведёт биллинг, наблюдатель только
            смотрит.
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Книги</h2>
          </div>
          {ledger.isLoading ? (
            <div className="state">Загрузка…</div>
          ) : (ledger.data ?? []).length === 0 ? (
            <div className="state">Проводок ещё не было</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Счёт</th>
                  <th>Тип</th>
                  <th className="num">Остаток</th>
                </tr>
              </thead>
              <tbody>
                {(ledger.data ?? []).map((account) => (
                  <tr key={account.id}>
                    <td>
                      <code>{account.code}</code>
                    </td>
                    <td className="muted small">{ACCOUNT_LABELS[account.type] ?? account.type}</td>
                    <td className="num">{money(account.balanceKopecks)} ₽</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="form-hint">
            Все остатки посчитаны по проводкам — их нельзя поправить мимо операций.
          </div>
        </div>
      </div>

      {inviting && (
        <Modal title="Новый участник" onClose={() => setInviting(false)}>
          <div className="form-group">
            <label className="form-label" htmlFor="invite-user">
              Идентификатор пользователя
            </label>
            <input
              id="invite-user"
              className="input"
              value={invite.userId}
              onChange={(e) => setInvite({ ...invite, userId: e.target.value.trim() })}
              placeholder="UUID из каталога пользователей"
              autoFocus
            />
            <div className="form-hint">
              Поиска по людям здесь нет: биллинг знает только идентификаторы, каталог живёт в subjects.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="invite-role">
              Роль
            </label>
            <select
              id="invite-role"
              className="input"
              value={invite.role}
              onChange={(e) => setInvite({ ...invite, role: e.target.value as TenantRole })}
            >
              <option value="OWNER">Владелец — настройки и состав</option>
              <option value="ADMIN">Администратор — ведение биллинга</option>
              <option value="VIEWER">Наблюдатель — только чтение</option>
            </select>
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setInviting(false)}>
              Отмена
            </button>
            <button
              className="btn btn-primary"
              disabled={!invite.userId || addMember.isPending}
              onClick={() => { setError(null); addMember.mutate(); }}
            >
              Добавить
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'владелец',
  ADMIN: 'администратор',
  VIEWER: 'наблюдатель',
};

const ACCOUNT_LABELS: Record<string, string> = {
  ASSET: 'деньги',
  RECEIVABLE: 'дебиторка',
  REVENUE: 'выручка',
  LIABILITY: 'обязательства',
};
